import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Box, Button, Dialog, DialogActions, DialogContent, DialogTitle,
  IconButton, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TextField, Typography, Paper, Snackbar, Alert
} from '@mui/material';
import { Add, Edit, Delete } from '@mui/icons-material';
import { BACKEND_BASE_URL } from '../config';

const YoutubeAPIKeyManager = () => {
  const [apiKeys, setApiKeys] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingKey, setEditingKey] = useState(null);
  const [formData, setFormData] = useState({ name: '', api_key: '' });
  const [formErrors, setFormErrors] = useState({ name: '', api_key: '' });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const fetchAPIKeys = async () => {
    try {
      const response = await axios.get(`${BACKEND_BASE_URL}/api/youtube-api-keys/`);
      setApiKeys(response.data);
    } catch (error) {
      showSnackbar('Failed to fetch API keys.', 'error');
    }
  };

  useEffect(() => {
    fetchAPIKeys();
  }, []);

  const handleOpen = (key = null) => {
    setEditingKey(key);
    setFormData(key ? { name: key.name, api_key: key.api_key } : { name: '', api_key: '' });
    setFormErrors({});
    setOpenDialog(true);
  };

  const handleClose = () => {
    setOpenDialog(false);
    setEditingKey(null);
  };

  const handleChange = e => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.name.trim()) errors.name = 'Name is required';
    if (!formData.api_key.trim()) errors.api_key = 'API key is required';
    // Optionally add a pattern validation here
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    try {
      if (editingKey) {
        await axios.put(`${BACKEND_BASE_URL}/api/youtube-api-keys/${editingKey.id}/`, formData);
        showSnackbar('API key updated successfully!');
      } else {
        await axios.post(`${BACKEND_BASE_URL}/api/youtube-api-keys/`, formData);
        showSnackbar('API key added successfully!');
      }
      fetchAPIKeys();
      handleClose();
    } catch (err) {
      showSnackbar(err.response?.data?.detail || 'Operation failed.', 'error');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this key?')) {
      try {
        await axios.delete(`${BACKEND_BASE_URL}/api/youtube-api-keys/${id}/`);
        fetchAPIKeys();
        showSnackbar('API key deleted successfully!');
      } catch {
        showSnackbar('Failed to delete API key.', 'error');
      }
    }
  };

  return (
    <Box p={4}>
      {/* Header and Add button */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h4" sx={{ fontSize: { xs: '1.5rem', sm: '2rem' } }}>Youtube API Key Management</Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => handleOpen()}
          sx={{
            backgroundColor: '#152F8F',   // Background color
            color: 'white',               // Text color
            fontWeight: 'bold',           // Bold text
            fontSize: { xs: '0.75rem', sm: '1rem' },  // Smaller font size on mobile
            padding: { xs: '6px 16px', sm: '8px 20px' }, // Adjust padding for smaller screens
            minWidth: 'auto',             // Let the button size adjust to text
            '&:hover': {
              backgroundColor: '#0d1d56', // Darker color on hover
            },
            alignSelf: 'flex-end',        // Align to the right on mobile
          }}
        >
          Add New Key
        </Button>

      </Box>

      {/* Table to display API Keys */}
      <TableContainer component={Paper} elevation={3} sx={{ overflowX: 'auto' }}>
        <Table>
          <TableHead sx={{ backgroundColor: '#152F8F' }}>
            <TableRow>
              <TableCell sx={{ color: 'white' }}><strong>Name</strong></TableCell>
              <TableCell sx={{ color: 'white' }}><strong>API Key</strong></TableCell>
              <TableCell sx={{ color: 'white' }} align="right"><strong>Actions</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {apiKeys.map((key) => (
              <TableRow key={key.id} hover sx={{ backgroundColor: 'white' }}>
                <TableCell>{key.name}</TableCell>
                <TableCell>{key.api_key}</TableCell>
                <TableCell align="right">
                  <IconButton onClick={() => handleOpen(key)} color="primary"><Edit /></IconButton>
                  <IconButton onClick={() => handleDelete(key.id)} color="error"><Delete /></IconButton>
                </TableCell>
              </TableRow>
            ))}
            {apiKeys.length === 0 && (
              <TableRow>
                <TableCell colSpan={3} align="center">No API Keys Found</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Dialog for add/edit */}
      <Dialog open={openDialog} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>{editingKey ? 'Edit API Key' : 'Add API Key'}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <TextField
            label="Name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            error={!!formErrors.name}
            helperText={formErrors.name}
            fullWidth
          />
          <TextField
            label="API Key"
            name="api_key"
            value={formData.api_key}
            onChange={handleChange}
            error={!!formErrors.api_key}
            helperText={formErrors.api_key}
            fullWidth
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleClose}>Cancel</Button>
          <Button variant="contained" onClick={handleSubmit}>{editingKey ? 'Update' : 'Add'}</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for alerts */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default YoutubeAPIKeyManager;
